import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, CheckCircle, XCircle, Clock, Loader2, Trash2, Globe } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

const leaveTypes = {
  annual: "Annual Leave",
  sick: "Sick Leave",
  personal: "Personal Leave",
  unpaid: "Unpaid Leave",
};

const statusConfig = {
  pending: { label: "Pending", className: "badge-pending", icon: Clock },
  approved: { label: "Approved", className: "badge-approved", icon: CheckCircle },
  rejected: { label: "Rejected", className: "badge-rejected", icon: XCircle },
};

export default function AdminLeaves() {
  const { api } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [adminComment, setAdminComment] = useState("");

  const fetchLeaves = async () => {
    try {
      const response = await api().get("/admin/leaves");
      setLeaves(response.data);
    } catch (error) {
      toast.error("Failed to load leave requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const handleAction = async (leaveId, status) => {
    setProcessing(true);
    try {
      await api().put(`/admin/leaves/${leaveId}`, {
        status,
        admin_comment: adminComment,
      });
      toast.success(`Leave request ${status}!`);
      setSelectedLeave(null);
      setAdminComment("");
      fetchLeaves();
    } catch (error) {
      toast.error(`Failed to ${status} leave request`);
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async (leaveId) => {
    try {
      await api().delete(`/admin/leaves/${leaveId}`);
      toast.success("Leave request deleted!");
      fetchLeaves();
    } catch (error) {
      toast.error("Failed to delete leave request");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const pendingLeaves = leaves.filter(l => l.status === "pending");
  const processedLeaves = leaves.filter(l => l.status !== "pending");

  return (
    <div className="space-y-8" data-testid="admin-leaves">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Leave Management</h1>
        <p className="text-slate-500 mt-1">Review and manage leave requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-800 font-mono">{pendingLeaves.length}</p>
                <p className="text-sm text-amber-700">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {leaves.filter(l => l.status === "approved").length}
                </p>
                <p className="text-sm text-slate-500">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-50 rounded-lg">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">
                  {leaves.filter(l => l.status === "rejected").length}
                </p>
                <p className="text-sm text-slate-500">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Requests */}
      {pendingLeaves.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <Clock className="h-5 w-5" />
              Pending Requests ({pendingLeaves.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pendingLeaves.map((leave) => (
              <div
                key={leave.id}
                className="p-4 bg-white border border-amber-200 rounded-lg"
                data-testid={`pending-leave-${leave.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h4 className="font-semibold text-slate-900">{leave.user_name}</h4>
                      <Badge variant="outline">{leaveTypes[leave.leave_type]}</Badge>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <CalendarDays className="h-4 w-4" />
                      {formatSydney(leave.start_date, "MMM dd")} - {formatTableDateSydney(leave.end_date)}
                    </div>
                    <p className="text-sm text-slate-500">{leave.reason}</p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setSelectedLeave({ ...leave, action: "approved" })}
                      data-testid={`approve-leave-${leave.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setSelectedLeave({ ...leave, action: "rejected" })}
                      data-testid={`reject-leave-${leave.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* All Requests Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">All Leave Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Type</th>
                  <th>Dates</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Comment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaves.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      No leave requests
                    </td>
                  </tr>
                ) : (
                  leaves.map((leave) => {
                    const status = statusConfig[leave.status];
                    return (
                      <tr key={leave.id} data-testid={`leave-row-${leave.id}`}>
                        <td className="font-medium">{leave.user_name}</td>
                        <td>{leaveTypes[leave.leave_type]}</td>
                        <td>
                          {formatSydney(leave.start_date, "MMM dd")} - {formatSydney(leave.end_date, "MMM dd")}
                        </td>
                        <td className="max-w-[150px] truncate">{leave.reason}</td>
                        <td>
                          <Badge className={status.className}>{status.label}</Badge>
                        </td>
                        <td className="text-slate-500 text-sm">
                          {leave.admin_comment || "-"}
                        </td>
                        <td>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:bg-red-50"
                                data-testid={`delete-leave-${leave.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Leave Request?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will permanently delete the leave request for {leave.user_name} ({formatSydney(leave.start_date, "MMM dd")} - {formatSydney(leave.end_date, "MMM dd")}). This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(leave.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Action Dialog */}
      <Dialog open={!!selectedLeave} onOpenChange={() => setSelectedLeave(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedLeave?.action === "approved" ? "Approve" : "Reject"} Leave Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">{selectedLeave?.user_name}</p>
              <p className="text-sm text-slate-500">{leaveTypes[selectedLeave?.leave_type]}</p>
              <p className="text-sm text-slate-500 mt-1">
                {selectedLeave && `${formatSydney(selectedLeave.start_date, "MMM dd")} - ${formatTableDateSydney(selectedLeave.end_date)}`}
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Comment (optional)</label>
              <Textarea
                placeholder="Add a comment..."
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                rows={3}
                data-testid="admin-comment-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setSelectedLeave(null)}
              >
                Cancel
              </Button>
              <Button
                className={`flex-1 ${selectedLeave?.action === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
                disabled={processing}
                onClick={() => handleAction(selectedLeave?.id, selectedLeave?.action)}
                data-testid="confirm-action-btn"
              >
                {processing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : selectedLeave?.action === "approved" ? (
                  "Approve"
                ) : (
                  "Reject"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
